import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date): string {
  // Use local timezone (not UTC) to avoid date shifting in non-UTC timezones
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Normalize text for substring search: strip diacritics (Greek tonos, etc.),
 * lowercase, and fold final sigma ς → σ so typed names match stored names.
 */
export function normalizeForSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/ς/g, "σ");
}

/** Substring match using {@link normalizeForSearch} on both sides (titles, guest names, etc.). */
export function textMatchesSearch(haystack: string | null | undefined, rawNeedle: string): boolean {
  const needle = rawNeedle.trim();
  if (!needle) return true;
  return normalizeForSearch(haystack || "").includes(normalizeForSearch(needle));
}

/**
 * Customer list / combobox search: accent-insensitive name match; phone via raw substring
 * or digit-only match (same intent as the original ReservationForm filters).
 */
export function customerMatchesSearch(
  customer: { name?: string | null; phone?: string | null },
  rawSearch: string,
): boolean {
  const trimmed = rawSearch.trim();
  if (!trimmed) return true;
  if (normalizeForSearch(customer.name || "").includes(normalizeForSearch(trimmed))) return true;
  const phone = customer.phone || "";
  if (phone.includes(trimmed)) return true;
  const digitsQ = trimmed.replace(/\D/g, "");
  const digitsP = phone.replace(/\D/g, "");
  if (digitsQ.length >= 1 && digitsP.includes(digitsQ)) return true;
  return false;
}
